import { useState, useRef, useEffect } from 'react';
import { useToast } from '@practium/ui';
import type { ResultTableRow } from '../types';
import { buildCalculationsSavedMessage } from '../services/correctionSaveFeedback';
import { useSaveCorrectionAdvice } from './useSaveCorrectionAdvice';

interface SchemaLike {
	safeParse: (data: unknown) => {
		success: boolean;
		error?: { issues: Array<{ path: (string | number | symbol)[]; message: string }> };
	};
}

export interface AjusteData {
	dose: string;
	doseSecundaria: string;
}

interface CalculationResult {
	ajuste: AjusteData;
	table: ResultTableRow[];
}

interface UseTabCalculationConfig<TForm, TResult extends CalculationResult> {
	form: TForm;
	ajuste: AjusteData;
	result: TResult | null;
	hasCalculated: boolean;
	setField: (key: keyof TForm, value: string) => void;
	setAjusteField: (key: 'dose' | 'doseSecundaria', value: string) => void;
	setResult: (result: TResult | null) => void;
	setHasCalculated: (v: boolean) => void;
	reset: () => void;
	schema: SchemaLike;
	calculate: (form: TForm) => Promise<TResult>;
	tabName: string;
	onSetField?: (key: keyof TForm, value: string) => void;
}

export const useTabCalculation = <TForm, TResult extends CalculationResult>({
	form,
	ajuste,
	result,
	hasCalculated,
	setField,
	setAjusteField,
	setResult,
	setHasCalculated,
	reset,
	schema,
	calculate,
	tabName,
	onSetField,
}: UseTabCalculationConfig<TForm, TResult>) => {
	const toast = useToast();
	const { save } = useSaveCorrectionAdvice();
	const [isCalculating, setIsCalculating] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const formRef = useRef(form);
	const schemaRef = useRef(schema);
	const calculateRef = useRef(calculate);

	useEffect(() => {
		formRef.current = form;
		schemaRef.current = schema;
		calculateRef.current = calculate;
	});

	const set = (key: keyof TForm, value: string) => {
		setField(key, value);
		onSetField?.(key, value);
		setErrors((e) => ({ ...e, [key as string]: '' }));
	};

	const setAjuste = (key: 'dose' | 'doseSecundaria', value: string) => setAjusteField(key, value);

	const validate = (): boolean => {
		const validation = schemaRef.current.safeParse(formRef.current);
		if (!validation.success) {
			const fieldErrors: Record<string, string> = {};
			validation.error?.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			return false;
		}
		setErrors({});
		return true;
	};

	const handleCalculate = async () => {
		if (!validate()) return;
		setIsCalculating(true);
		try {
			const res = await calculateRef.current(formRef.current);
			setResult(res);
			setAjusteField('dose', res.ajuste.dose);
			setAjusteField('doseSecundaria', res.ajuste.doseSecundaria);
			setHasCalculated(true);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : `Erro ao calcular ${tabName}.`);
		} finally {
			setIsCalculating(false);
		}
	};

	const handleSave = async (): Promise<boolean> => {
		if (!hasCalculated) {
			toast.error('Execute o cálculo antes de salvar.');
			return false;
		}
		if (!validate()) return false;
		setIsSaving(true);
		try {
			const { calculations } = await save();
			toast.success(buildCalculationsSavedMessage(calculations));
			return true;
		} catch {
			toast.error(`Erro ao salvar ${tabName}.`);
			return false;
		} finally {
			setIsSaving(false);
		}
	};

	const handleClear = () => {
		reset();
		setErrors({});
	};

	return {
		form,
		ajuste,
		result,
		hasCalculated,
		isCalculating,
		isSaving,
		errors,
		set,
		setAjuste,
		handleCalculate,
		handleSave,
		handleClear,
	};
};
